'use client'

import { useEffect } from 'react'
import { pushEvent } from '@/lib/gtm'

/**
 * Praćenje ponašanja na početnoj stranici.
 *
 * Sekcije se označavaju s `data-track-section`, dugmad s `data-track-cta`.
 * Vrijednosti su fiksni ključevi, isti za bs i en.
 *
 * Događaji:
 *  - landing_scroll        — dubina skrolanja (percent: 25, 50, 75, 100)
 *  - landing_section_view  — korisnik se zadržao na sekciji bar 2 sekunde (section)
 *  - landing_section_time  — pri izlasku, ukupno sekundi po sekciji (section, seconds)
 *  - landing_cta_click     — klik na označeno dugme (cta, section, seconds_before_click)
 */

const SCROLL_MILESTONES = [25, 50, 75, 100]
const SECTION_VIEW_SECONDS = 2

export function LandingTracker() {
  useEffect(() => {
    const sections = () => Array.from(document.querySelectorAll<HTMLElement>('[data-track-section]'))

    // Sekcija koja prelazi sredinu ekrana je ona koju korisnik trenutno gleda.
    const currentSection = () => {
      const middle = window.innerHeight / 2
      const el = sections().find(section => {
        const rect = section.getBoundingClientRect()
        return rect.top <= middle && rect.bottom >= middle
      })
      return el?.dataset.trackSection
    }

    // Vrijeme po sekciji — broji se samo dok je tab vidljiv
    let activeSeconds = 0
    const sectionSeconds: Record<string, number> = {}
    const viewed = new Set<string>()
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return
      activeSeconds += 1
      const section = currentSection()
      if (!section) return
      sectionSeconds[section] = (sectionSeconds[section] ?? 0) + 1
      if (sectionSeconds[section] >= SECTION_VIEW_SECONDS && !viewed.has(section)) {
        viewed.add(section)
        pushEvent('landing_section_view', { section })
      }
    }, 1000)

    // Dubina skrolanja
    const reachedScroll = new Set<number>()
    const onScroll = () => {
      const doc = document.documentElement
      const scrollable = doc.scrollHeight - window.innerHeight
      const percent = scrollable <= 0 ? 100 : Math.round((window.scrollY / scrollable) * 100)
      for (const milestone of SCROLL_MILESTONES) {
        if (percent >= milestone && !reachedScroll.has(milestone)) {
          reachedScroll.add(milestone)
          pushEvent('landing_scroll', { percent: milestone })
        }
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })

    // Klikovi na označena dugmad
    const onClick = (e: MouseEvent) => {
      const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-track-cta]')
      if (!el) return
      const section = el.closest<HTMLElement>('[data-track-section]')?.dataset.trackSection
      pushEvent('landing_cta_click', { cta: el.dataset.trackCta, section, seconds_before_click: activeSeconds })
    }
    document.addEventListener('click', onClick)

    // Izlaz sa stranice — šalje se jednom, i kad korisnik ode na drugu stranicu unutar sajta
    let exitSent = false
    const onExit = () => {
      if (exitSent) return
      exitSent = true
      for (const [section, seconds] of Object.entries(sectionSeconds)) {
        if (seconds >= SECTION_VIEW_SECONDS) pushEvent('landing_section_time', { section, seconds })
      }
    }
    window.addEventListener('pagehide', onExit)

    return () => {
      onExit()
      window.clearInterval(timer)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('pagehide', onExit)
      document.removeEventListener('click', onClick)
    }
  }, [])

  return null
}
